import React, { PureComponent, memo } from 'react';
import PropTypes from 'prop-types';
import { connect, ReactReduxContext } from 'react-redux';
import classNames from 'classnames';
import {
  withGoogleMap,
  GoogleMap,
  Marker,
  Polyline,
  Polygon,
  InfoWindow,
  StreetViewPanorama,
  OverlayView,
  Circle
} from 'react-google-maps';
import { DrawingManager } from 'react-google-maps/lib/components/drawing/DrawingManager';
import { Map as Map2, fromJS, List } from 'immutable';
import { compose, withProps } from 'recompose';
import config from 'config';
import { MAP_NO_METRIC, searchParcel } from 'data/search';
import * as onKeyPress from 'utils/DOM/onKeyPress';
import SVG from 'components/base/SVG';
import Button from 'components/base/Button';
import withProperty from 'app/components/withProperty';
import PropertyInfo from './PropertyInfo';
import Analytics from './Analytics';
import css from './style.scss';
import { withRouter } from 'react-router-dom';
import DrawActive from '../../../assets/images/DrawActive.svg';
import DrawHover from '../../../assets/images/DrawHover.svg';
import PencilIcon from '../../../assets/images/PencilIcon.svg';
import iconClose from '../../../assets/images/iconCloses.svg';
import WhiteClose from '../../../assets/images/WhiteClose.svg';
import ReactDOM from 'react-dom';

const {
  constants: {
    API: { BASE_URL },
  },
} = config;

const MIN_PARCEL_ZOOM = 19;
const emptyMap = Map2();

const drawProps = {
  strokeColor: '#128292',
  strokeWeight: 2,
  strokeOpacity: 0.8,
  clickable: false,
  fillColor: '#E1F8FA',
  fillOpacity: 0.3,
  geodesic: false
};

const parseShape = (def) =>
    def.split('/').map((p) => ({
      lat: Number(p.substr(0, p.indexOf(','))),
      lng: Number(p.substr(p.indexOf(',') + 1)),
    }));

const tileXYToQuadKey = (x, y, z) => {
  let quadKey = '';
  for (let i = z; i > 0; i--) {
    let digit = 0;

    const mask = 1 << (i - 1);
    if (x & mask) digit++;
    if (y & mask) digit += 2;

    quadKey = `${quadKey}${digit}`;
  }

  return quadKey;
};

// Updated formatValue to remove the '$' sign
function formatValue(val) {
  if (typeof val !== 'number' || isNaN(val)) return 'N/A';
  if (val >= 1_000_000_000)
    return `${(val / 1_000_000_000).toFixed(
        val < 10_000_000_000 ? 2 : val < 100_000_000_000 ? 1 : 0
    )}B`;
  if (val >= 1_000_000) {
    const mil = val / 1_000_000;
    return `${mil < 10 ? mil.toFixed(2) : mil.toFixed(1)}M`;
  }
  if (val >= 1_000) {
    const thou = val / 1_000;
    if (val < 10_000)
      return `${(Math.floor(thou * 100) / 100).toFixed(2)}K`;
    if (val < 100_000)
      return `${(Math.floor(thou * 10) / 10).toFixed(1)}K`;
    return `${Math.floor(thou)}K`;
  }
  return `${val}`;
}


const calcDims = (mini, sel, hov) => {
  const isOpenedOrHovered = sel;
  if (mini) {
    // Number pins (mini)
    return {
      iconWidth: isOpenedOrHovered ? 43 : 30,
      iconHeight: isOpenedOrHovered ? 32 : 20,
    };
  } else {
    // Est value pins (normal)
    return {
      iconWidth: isOpenedOrHovered ? 68 : 48,
      iconHeight: isOpenedOrHovered ? 32 : 20,
    };
  }
};

const chooseFont = (mini, len, sel) => {
  if (!mini) return sel ? '16px' : '12px';
  if (len <= 3) return sel ? '16px' : '12px';
  if (len <= 6) return sel ? '10px' : '8px';
  return sel ? '8px' : '6px';
};

/* ------------------------------------------------------------------ */
/* ------ memoised marker to minimise re-renders -------------------- */
const MemoMarker = memo(
    ({
       position,
       label,
       zIndex,
       isHovered,
       isSelected,
       onOver,
       onOut,
       onClick,
       children,
       icon,
     }) => (
        <Marker
            position={position}
            optimized={false}
            icon={icon}
            label={label}
            zIndex={zIndex}
            onMouseOver={onOver}
            onMouseOut={onOut}
            onClick={onClick}
        >
          {children}
        </Marker>
    ),
    (prev, next) =>
        prev.isHovered === next.isHovered &&
        prev.isSelected === next.isSelected &&
        prev.zIndex === next.zIndex &&
        prev.label.text === next.label.text &&
        prev.label.className === next.label.className &&
        prev.label.fontSize === next.label.fontSize &&
        prev.icon.scaledSize.width === next.icon.scaledSize.width &&
        prev.icon.scaledSize.height === next.icon.scaledSize.height &&
        prev.children === next.children
);
/* ------------------------------------------------------------------ */

class Map extends PureComponent {
  static contextType = ReactReduxContext;

  constructor(props) {
    super(props);

    this.map = null;
    this.innerMap = null;
    this.mapContainer = null;
    this.currentLatLng = null;

    this.state = {
      bounds: '',
      drawing: false,
      drawCoords: [],
      cursorCoord: null,
      infoProperty: null,
      parcelProperty: null,
      metric: MAP_NO_METRIC,
      markersRendered: 0,
      renderTimer: null,
      hoveredMarkerId: null,
      streetViewVisible: false,
      streetViewPosition: null,
      hovered: false,
      drawMode: null,
      showDrawMenu: false,
      showToast: false,
      handfreeDrawMode: false,
      isHandfreedrawing: false,
      isCircleDrawing: false,
      mapContainer: null,
      isFullMapView: false,
    };

    this.defaultOptions = Object.freeze({
      mapTypeControlOptions: {
        position: window.google.maps.ControlPosition.LEFT_BOTTOM,
      },
      draggableCursor: '',
      draggable: true,
      tilt: 0,
    });
    this.drawOptions = Object.freeze({
      // ...this.defaultOptions,
      mapTypeControlOptions: {
        position: window.google.maps.ControlPosition.LEFT_BOTTOM,
      },
      draggableCursor: 'crosshair',
      draggable: false,
    });
    this.handfreeOptions = Object.freeze({
      mapTypeControlOptions: {
        position: window.google.maps.ControlPosition.LEFT_BOTTOM,
      },
      draggableCursor: `url(${PencilIcon}) 8 16, auto`,
      draggable: false,
    });
    this.circleOptions = Object.freeze({
      mapTypeControlOptions: {
        position: window.google.maps.ControlPosition.LEFT_BOTTOM,
      },
      draggableCursor: 'crosshair',
      draggable: false,
    });

    this.onRef = this.onRef.bind(this);
    this.zoomToFit = this.zoomToFit.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleRightClick = this.handleRightClick.bind(this);
    this.handleDrawBegin = this.handleDrawBegin.bind(this);
    this.handleDrawCancel = this.handleDrawCancel.bind(this);
    this.handleRemoveBoundary = this.handleRemoveBoundary.bind(this);
    this.handleDrawComplete = this.handleDrawComplete.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMetricChange = this.handleMetricChange.bind(this);
    this.handleInfoClose = this.handleInfoClose.bind(this);
    this.handleSaveProperty = this.handleSaveProperty.bind(this);
    this.handleDrawIconEnter = () => this.setState({ hovered: true });
    this.handleDrawIconLeave = () => this.setState({ hovered: false });
    this.handleHandfreeDrawIconClick = () =>
        this.state.handfreeDrawMode ? this.handleHandfreedrawModeCancel() : this.handleHandfreedrawModeBegin();
    this.handleDrawIconClick = () => {
      this.setState(prev => {
        const nextShowDrawMenu = !prev.showDrawMenu;
        return {
          showDrawMenu: nextShowDrawMenu,
          showToast: nextShowDrawMenu ? prev.showToast : false,
        };
      });
    };
  }

  componentDidMount() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
          this.onLocation,
          this.onLocationError,
          { timeout: 3000 }
      );
    }

    this.onEsc = () => {
      if (this.state.drawing) this.handleDrawCancel();
      if (this.state.handfreeDrawing) this.handleHandfreeDrawComplete();
      if (this.state.handfreeDrawMode) this.handleHandfreedrawModeCancel();
    };

    this.onEnter = () => {
      if (this.state.drawing) this.handleDrawComplete();
      if (this.state.handfreeDrawing) this.handleHandfreeDrawComplete();
    };

    onKeyPress.addListener('Escape', this.onEsc);
    onKeyPress.addListener('Enter', this.onEnter);

    if (this.props.onModelRender === true) {
      this.startMarkerAnimationWithDelay();
    }
  }

  componentWillUnmount() {
    onKeyPress.removeListener('Escape', this.onEsc);
    onKeyPress.removeListener('Enter', this.onEnter);

    if (this.mapContainer) {
      this.mapContainer.onmousedown = null;
      this.mapContainer.onmouseleave = null;
    }
    document.removeEventListener('mouseup', this.handleMapMouseUp);

    if (this.mouseDownListener) this.innerMap.removeListener(this.mouseDownListener);
    if (this.mouseUpListener) this.innerMap.removeListener(this.mouseUpListener);

    if (this.state.renderTimer) cancelAnimationFrame(this.state.renderTimer);
    if (this.delayTimer) clearTimeout(this.delayTimer);

    if (this.handleFullscreenChange) {
      document.removeEventListener("fullscreenchange", this.handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", this.handleFullscreenChange);
      document.removeEventListener("msfullscreenchange", this.handleFullscreenChange);
    }
  }

  componentDidUpdate(prevProps) {
    const { properties, activeResult, reset } = this.props;
    const { infoProperty } = this.state;

    if (this.props.reset && !prevProps.reset) {
      this.setState({ infoProperty: null, parcelProperty: null }, () => {
        if (this.props.onResetComplete) {
          this.props.onResetComplete();
        }
      });
      this.handleDrawCancel();
    }

    if (this.props.properties && (!prevProps.properties || this.props.properties.size > prevProps.properties.size)) {
      this.startMarkerAnimationWithDelay();
    }

    if (prevProps.shapeDefinition && !this.props.shapeDefinition)
      this.setState({ drawCoords: [] });
    else if (this.props.shapeDefinition && !this.props.hidePolygon && prevProps.shapeDefinition !== this.props.shapeDefinition) {
      const def = this.props.shapeDefinition;
      this.setState({
        drawCoords: Array.isArray(def)
            ? def.map((p) => ({
              lat: Number(p.substr(0, p.indexOf(','))),
              lng: Number(p.substr(p.indexOf(',') + 1)),
            }))
            : parseShape(def),
      });
    } else if (this.props.hidePolygon && this.state.drawCoords.length) {
      this.setState({ drawCoords: [] });
    } else if (!this.props.hidePolygon && prevProps.hidePolygon && this.props.shapeDefinition) {
      this.setState({
        drawCoords: parseShape(this.props.shapeDefinition),
      });
    }

    let infoProp = null;
    (properties || []).forEach((p) => {
      if (activeResult && activeResult !== prevProps.activeResult) {
        if (activeResult === p.get('id')) infoProp = p;
      } else if (infoProperty && infoProperty.get('id') === p.get('id'))
        infoProp = p;
    });

    if (!infoProp && activeResult) {
      infoProp = (properties || []).find((p) => p.get('id') === activeResult);
    }

    if (infoProp && infoProp !== infoProperty && this.props.autoOpenInfo) {
      this.setInfoProperty(infoProp);
    }

    const { errors } = this.props;
    if (errors && errors !== prevProps.errors) {
      this.handleDrawCancel();
    }

    this.zoomToFit();
  }

  clearInfoProperty = () => {
    this.setState({ infoProperty: null });
  };

  startMarkerAnimationWithDelay = () => {
    clearTimeout(this.delayTimer);
    this.setState({ markersRendered: 0 });
    this.delayTimer = setTimeout(this.startMarkerAnimation, 2000);
  };

  startMarkerAnimation = () => {
    if (this.state.renderTimer) cancelAnimationFrame(this.state.renderTimer);
    const { tempRangeTo, pageCount, pageIndex, pageSize = 250, properties } = this.props;
    if (tempRangeTo && pageIndex === pageCount) {
      this.state.markersRendered = 0;
    }
    let maxMarkers = properties?.size || 0;
    if (tempRangeTo && pageIndex === pageCount) {
      const rowsShownBefore = (pageIndex - 1) * pageSize + (this.props.tempRangeFrom ? this.props.tempRangeFrom - 1 : 0);
      maxMarkers = Math.max(0, tempRangeTo - rowsShownBefore);
    }
    this.setState(
        prev => ({ markersRendered: Math.min(prev.markersRendered, maxMarkers) }),
        () => {
          const step = () => {
            this.setState(prev => {
              const next = prev.markersRendered + 5;
              if (next >= maxMarkers) {
                return { markersRendered: maxMarkers, renderTimer: null };
              }
              const id = requestAnimationFrame(step);
              return { markersRendered: next, renderTimer: id };
            });
          };
          const id = requestAnimationFrame(step);
          this.setState({ renderTimer: id });
        }
    );
  };

  onRef(ref) {
    this.map = ref;

    if (!this.map) return;

    this.innerMap =
        this.map.context.__SECRET_MAP_DO_NOT_USE_OR_YOU_WILL_BE_FIRED;

    const mapDiv = this.innerMap.getDiv();

    if (!this.handleFullscreenChange) {
      this.handleFullscreenChange = () => {
        const isFull =
            document.fullscreenElement ||
            document.webkitFullscreenElement ||
            document.msFullscreenElement;

        this.setState({ isFullMapView: !!isFull });
      };

      document.addEventListener("fullscreenchange", this.handleFullscreenChange);
      document.addEventListener("webkitfullscreenchange", this.handleFullscreenChange);
      document.addEventListener("msfullscreenchange", this.handleFullscreenChange);
    }

    const setContainer = () => {
      const container =
          mapDiv.classList.contains("gm-style")
              ? mapDiv
              : mapDiv.querySelector(".gm-style");

      if (container) {
        if (!this.mapContainer) {
          // Use DOM events, but attach mouseup to document to catch releases outside the map
          container.onmousedown = this.handleMapMouseDown;
          document.addEventListener('mouseup', this.handleMapMouseUp);
          container.onmouseleave = this.handleMapMouseLeave;

          this.mapContainer = container;

          this.setState({ mapContainer: container }, () => {
            this.forceUpdate();
          });
        }
      } else {
        setTimeout(setContainer, 100);
      }
    };

    setContainer();

    setTimeout(this.zoomToFit, 1);
  }

  simplifyCoords = (coords, tolerance) => {
    if (coords.length <= 2) return coords;

    const getPerpendicularDistance = (pt, lineStart, lineEnd) => {
      const x = pt.lat(), y = pt.lng();
      const x1 = lineStart.lat(), y1 = lineStart.lng();
      const x2 = lineEnd.lat(), y2 = lineEnd.lng();

      const dx = x2 - x1;
      const dy = y2 - y1;

      if (dx === 0 && dy === 0) return 0;

      return Math.abs(dy * x - dx * y + x2 * y1 - y2 * x1) /
          Math.sqrt(dx * dx + dy * dy);
    };

    const rdp = (points) => {
      if (points.length < 3) return points;

      let maxDist = 0;
      let index = 0;

      for (let i = 1; i < points.length - 1; i++) {
        const dist = getPerpendicularDistance(
            points[i],
            points[0],
            points[points.length - 1]
        );
        if (dist > maxDist) {
          index = i;
          maxDist = dist;
        }
      }

      if (maxDist > tolerance) {
        const left = rdp(points.slice(0, index + 1));
        const right = rdp(points.slice(index));
        return [...left.slice(0, -1), ...right];
      }

      return [points[0], points[points.length - 1]];
    };

    return rdp(coords);
  };

  optimizePolygon = (coords) => {
    if (!coords || coords.length < 3) return coords;

    const MAX_POINTS = 40;

    // Remove exact duplicate consecutive points only — no zoom-dependent thinning
    // so zoomed-in shapes keep their fine detail going into the simplifier.
    const reduced = [coords[0]];
    for (let i = 1; i < coords.length; i++) {
      const prev = reduced[reduced.length - 1];
      const curr = coords[i];
      const dx = curr.lat() - prev.lat();
      const dy = curr.lng() - prev.lng();
      if (dx !== 0 || dy !== 0) reduced.push(curr);
    }

    // If already within limit, just close and return.
    if (reduced.length <= MAX_POINTS) {
      return this.closePolygon(reduced);
    }

    // Adaptive RDP: binary-search for the *minimum* tolerance that brings the
    // point count to ≤ MAX_POINTS.  This preserves the maximum possible shape
    // fidelity regardless of zoom level — a zoomed-in shape gets a tiny
    // tolerance; a zoomed-out shape gets a proportionally larger one.
    let lo = 0;
    let hi = 1;
    while (this.simplifyCoords(reduced, hi).length > MAX_POINTS) hi *= 2;

    for (let i = 0; i < 20; i++) {
      const mid = (lo + hi) / 2;
      if (this.simplifyCoords(reduced, mid).length > MAX_POINTS) {
        lo = mid;
      } else {
        hi = mid;
      }
    }

    return this.closePolygon(this.simplifyCoords(reduced, hi));
  };

  closePolygon = (points) => {
    if (points.length > 2) {
      const first = points[0];
      const last = points[points.length - 1];
      if (first.lat() !== last.lat() || first.lng() !== last.lng()) {
        return [...points, first];
      }
    }
    return points;
  };

  onLocation(location) {
    console.log(this, location);
  }

  onLocationError(err) {
    console.log(this, err);
  }

  setInfoProperty(infoProperty = null) {
    this.setState({ infoProperty, parcelProperty: null });
  }

  handleMarkerMouseOver = (id) => {
    if (this.state.hoveredMarkerId !== id)
      this.setState({ hoveredMarkerId: id });
  };

  handleMarkerMouseOut = () => {
    if (this.state.hoveredMarkerId !== null)
      this.setState({ hoveredMarkerId: null });
  };

  handleClick(ev) {
    const { searchParcel, parcelClickEnabled } = this.props;
    const { drawing, drawCoords, handfreeDrawing, handfreeDrawMode, drawMode, isCircleDrawing } = this.state;
    const ll = ev.latLng;

    if (drawing) {
      this.setState({ drawCoords: [...drawCoords, ev.latLng] });
    } else if (drawMode === 'circle') {
      if (isCircleDrawing && this.state.circleRadius > 0) {
        this.handleCircleMouseUp();
      }
    } else if (!handfreeDrawing && !handfreeDrawMode && parcelClickEnabled && this.map.getZoom() >= MIN_PARCEL_ZOOM) {
      this.setState({ infoProperty: null, parcelProperty: null }, () => {
        searchParcel(ll.lat(), ll.lng(), ({ response }) => {
          let parcelProperty;
          if (response && response.id) {
            const { latitude, longitude, parcelBoundaryDefinition } = response;
            parcelProperty = fromJS(response).set('position', { lat: latitude, lng: longitude }).set('path', parseShape(parcelBoundaryDefinition));
          }
          this.setState({ parcelProperty });
        });
      });
    }
  }

  handleRightClick() {
    // if (this.state.drawing) this.handleDrawComplete();
    const { drawing, handfreeDrawing, isHandfreedrawing } = this.state;
    if (drawing) this.handleDrawComplete();
    if (handfreeDrawing) this.handleHandfreeDrawComplete();
    if (isHandfreedrawing) this.handleMouseUp();
  }

  handleDrawBegin() {
    this.setState({ drawing: true, drawCoords: [], cursorCoord: null })
  }

  handleRemoveBoundary() {
    this.handleDrawCancel();
    if (this.props.onRemoveBoundary) this.props.onRemoveBoundary();
  }

  handleDrawCancel() {
    // Reset both click-to-draw and handfree-pencil modes
    if (this.innerMap) {
      this.innerMap.setOptions({ draggable: true });
    }
    this.isDraggingCircle = false;
    this.setState({
      drawing: false,
      drawMode: null,
      drawCoords: [],
      cursorCoord: null,
      showDrawMenu: false,
      showToast: false,
      handfreeDrawMode: false,
      handfreeDrawing: false,
      isHandfreedrawing: false,
      isCircleDrawing: false,
      circleCenter: null,
      circleRadius: 0,
    });

  }

  handleDrawComplete() {
    const { drawing, drawCoords } = this.state;
    const { onDraw } = this.props;
    if (drawing) {
      if (drawCoords.length < 3) {
        this.handleDrawCancel();
      } else {
        this.setState({ drawing: false, cursorCoord: null });
        onDraw(drawCoords.map(c => `${c.lat()},${c.lng()}`));
        // this.setState({ drawCoords: [] });
      }
    }
  }

  handleHandfreeDrawCancel() {
    if (this.state.handfreeDrawing) this.setState({ handfreeDrawing: false, drawCoords: [] })
  }

  handleHandfreeDrawComplete() {
    const { handfreeDrawing, drawCoords } = this.state;
    const { onDraw } = this.props;
    if (handfreeDrawing) {
      if (drawCoords.length < 3) {
        this.handleHandfreeDrawCancel();
      } else {
        // Simplify only for free draw
        const optimized = this.optimizePolygon(drawCoords);
        this.setState({ handfreeDrawing: false, cursorCoord: null });
        onDraw(optimized.map(c => `${c.lat()},${c.lng()}`));
      }
    }
  }

  handleHandfreedrawModeBegin() {
    if (this.innerMap) {
      this.innerMap.setOptions({ draggable: false });
    }
    this.setState({ handfreeDrawMode: true, drawCoords: [], cursorCoord: null, isHandfreedrawing: false });
  }

  handleHandfreedrawModeCancel() {
    if (this.innerMap) {
      this.innerMap.setOptions({ draggable: true });
    }

    this.setState({
      handfreeDrawMode: false,
      handfreeDrawing: false,
      isHandfreedrawing: false,
      drawCoords: [],
      cursorCoord: null,
      drawing: false,
      drawMode: null,
      showDrawMenu: false,
      showToast: false,
    });
  }

  handleMouseDown(ev) {
    const { handfreeDrawMode } = this.state;
    if (handfreeDrawMode && ev.button === 0 && this.currentLatLng) {
      this.setState({ isHandfreedrawing: true, drawCoords: [this.currentLatLng] });
    }
  }

  handleMouseUp(ev) {
    const { isHandfreedrawing, drawCoords } = this.state;
    const { onDraw } = this.props;

    if (isHandfreedrawing && drawCoords.length >= 3) {
      this.setState({ handfreeDrawMode: false, isHandfreedrawing: false, cursorCoord: null });
      const optimized = this.optimizePolygon(drawCoords);

      onDraw(optimized.map(c => `${c.lat()},${c.lng()}`));
    } else if (isHandfreedrawing) {
      this.setState({ isHandfreedrawing: false });
    }
  }

  handleMapMouseDown = (ev) => {
    const { handfreeDrawMode, drawMode, isCircleDrawing } = this.state;
    if (handfreeDrawMode && ev.button === 0 && this.currentLatLng) {
      ev.preventDefault();
      ev.stopPropagation();
      this.setState({ isHandfreedrawing: true, drawCoords: [this.currentLatLng] });
    } else if (drawMode === 'circle' && ev.button === 0 && this.currentLatLng) {
      if (!isCircleDrawing) {
        this.setState({ isCircleDrawing: true, circleCenter: this.currentLatLng, circleRadius: 0 });
      }
      this.isDraggingCircle = true;
      ev.preventDefault();
      ev.stopPropagation();
    }
  }

  handleMapMouseUp = (ev) => {
    const { isHandfreedrawing, drawCoords, drawMode, isCircleDrawing, circleRadius, isDraggingCircle } = this.state;
    const { onDraw } = this.props;

    if (drawMode === 'circle') {
      if (isDraggingCircle) {
        this.isDraggingCircle = false;
        if (circleRadius > 0) {
          this.handleCircleMouseUp();
        } else {
          this.setState({
            isCircleDrawing: false,
            drawMode: null,
            showDrawMenu: false,
            showToast: false,
            circleCenter: null,
            circleRadius: 0,
          });
        }
      }
      // For non-drag clicks, don't cancel here - let user right-click or click again to finalize/cancel
      return;
    }

    if (isHandfreedrawing && drawCoords.length >= 3) {
      if (this.innerMap) {
        this.innerMap.setOptions({ draggable: true });
      }
      const optimized = this.optimizePolygon(drawCoords);
      this.setState({ handfreeDrawMode: false, isHandfreedrawing: false, cursorCoord: null });
      onDraw(optimized.map(c => `${c.lat()},${c.lng()}`));
    } else if (isHandfreedrawing) {
      this.setState({ isHandfreedrawing: false });
    }
  }

  handleMapMouseLeave = () => {
    const { isHandfreedrawing, handfreeDrawMode } = this.state;
    if (!isHandfreedrawing && !handfreeDrawMode) return;

    if (this.innerMap) {
      this.innerMap.setOptions({ draggable: true });
    }

    const nextState = {
      isHandfreedrawing: false,
      drawCoords: [],
      cursorCoord: null,
    };

    if (!handfreeDrawMode) {
      Object.assign(nextState, {
        handfreeDrawing: false,
        drawing: false,
        drawMode: null,
        showToast: false,
      });
    }

    this.setState(nextState);
  }

  handleMouseMove(ev) {
    const { drawing, drawCoords, handfreeDrawing, isHandfreedrawing } = this.state;

    this.currentLatLng = ev.latLng;

    if (drawing && drawCoords.length) this.setState({ cursorCoord: ev.latLng });
    if (handfreeDrawing) {
      const newCoords = [...drawCoords, ev.latLng];
      this.setState({ drawCoords: newCoords, cursorCoord: ev.latLng });
    }

    if (isHandfreedrawing) {
      const { drawCoords: coords } = this.state;
      const newCoords = [...coords, ev.latLng];
      this.setState({ drawCoords: newCoords, cursorCoord: ev.latLng });
    }
    this.handleCircleMouseMove(ev);
  }
  handleMetricChange(metric) {
    const overlays = this.innerMap.overlayMapTypes;
    if (overlays.length > 1) overlays.removeAt(1);
    if (metric !== MAP_NO_METRIC) overlays.push(new window.google.maps.ImageMapType({ getTileUrl: ({ x, y }, z) => `${BASE_URL}/resource/auth/ps4/map/tiles/${tileXYToQuadKey(x, y, z)}/statistics/${metric}` }));
    this.setState({ metric });
  }

  handleInfoClose = () => {
    this.setState({ infoProperty: null, parcelProperty: null });
    if (this.props.onCloseInfoWindow) this.props.onCloseInfoWindow();
  };

  zoomToFit() {
    const { properties, initialMapType } = this.props;
    const props = (properties || List()).filter(p => p.get('latitude'));
    if (!props.size) {
      this.setState({ bounds: null });
    } else {
      const bounds = new window.google.maps.LatLngBounds();
      props.forEach((p) => {
        bounds.extend({ lat: p.get('latitude'), lng: p.get('longitude') });
      });

      const boundsStr = bounds.toString();
      if (boundsStr !== this.state.bounds) {
        this.setState({ bounds: boundsStr }, () => {
          this.map.fitBounds(bounds);
          setTimeout(() => {
            if (initialMapType === 'roadmap' && this.map.getZoom() > 19) {
              this.innerMap.setZoom(19);
            }
          }, 10);
        });
      }
    }
  }

  handleSaveProperty = (propertyData) => {
    const { saveProperty } = this.props;
    saveProperty(null, propertyData);
  };

  handleStreetViewPositionChange = (e) => {
    const streetViewPosition = e.getCenter();
    this.setState({ streetViewPosition });
  };

  handleSelectDrawMode = (mode) => {
    if (this.innerMap) {
      this.innerMap.setOptions({ draggable: mode !== 'free' });
    }

    this.setState({
      drawMode: mode,
      drawing: false,

      handfreeDrawMode: false,
      handfreeDrawing: false,
      isHandfreedrawing: false,
      circleCenter: null,
      circleRadius: 0,
      isCircleDrawing: false,
      drawCoords: [],
      cursorCoord: null,
      showDrawMenu: true,
      showToast: true,
    });

    if (mode === 'free') {
      this.handleHandfreedrawModeBegin();
    }
  };

  handleDrawOptionMouseEnter = (mode) => {
    if (this.state.drawMode !== null) {
      this.setState({ showToast: this.state.drawMode === mode });
    }
  };

  handleDrawOptionMouseLeave = () => {
    if (this.state.drawMode !== null) {
      this.setState({ showToast: true });
    }
  };

  handleCircleMouseDown = (ev) => {
    const { drawMode } = this.state;
    if (drawMode !== 'circle') return;

    this.setState({
      isCircleDrawing: true,
      circleCenter: ev.latLng,
      circleRadius: 0,
    });
  }

  handleCircleMouseMove = (ev) => {
    const { isCircleDrawing, circleCenter } = this.state;
    if (!isCircleDrawing || !circleCenter) return;

    const radiusMeters = window.google.maps.geometry.spherical.computeDistanceBetween(
        circleCenter,
        ev.latLng
    );

    this.setState({ circleRadius: radiusMeters });
  };

  handleCircleMouseUp = () => {
    const { isCircleDrawing, circleCenter, circleRadius } = this.state;
    const { onDraw } = this.props;

    if (!isCircleDrawing || !circleCenter || circleRadius <= 0) {
      this.isDraggingCircle = false;
      return;
    }

    this.isDraggingCircle = false;

    const POINT_COUNT = 24;
    const coords = [];

    for (let i = 0; i < POINT_COUNT; i++) {
      const angle = (i * 360) / POINT_COUNT;
      const point = window.google.maps.geometry.spherical.computeOffset(
          circleCenter,
          circleRadius,
          angle
      );
      const lat = Math.round(point.lat() * 1e6) / 1e6;
      const lng = Math.round(point.lng() * 1e6) / 1e6;
      coords.push(`${lat},${lng}`);
    }

    coords.push(coords[0]);

    this.setState({
      isCircleDrawing: false,
      drawMode: null,
      showDrawMenu: false,
      showToast: false,
    });

    onDraw(coords);
  };

  handleRectangleComplete = (rectangle) => {
    const bounds = rectangle.getBounds();

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    const coords = [
      new window.google.maps.LatLng(ne.lat(), sw.lng()),
      new window.google.maps.LatLng(ne.lat(), ne.lng()),
      new window.google.maps.LatLng(sw.lat(), ne.lng()),
      new window.google.maps.LatLng(sw.lat(), sw.lng()),
      new window.google.maps.LatLng(ne.lat(), sw.lng()),
    ];

    // Remove rectangle overlay from map
    rectangle.setMap(null);

    this.setState({
      drawMode: null,
      drawCoords: coords,
      showDrawMenu: false,
      showToast: false,
    });

    if (this.props.onDraw) {
      this.props.onDraw(coords.map(c => `${c.lat()},${c.lng()}`));
    }
  };

  render() {
    const {
      properties,
      onDraw,
      infoEnabled,
      analyticsEnabled,
      initialZoom,
      initialMapType,
      propertySaved,
      loading,
      property,
      getProperty,
      getPropertyContact,
      rangeEnabled,
      selectionCount,
      onSelectionDetails,
      isStreetView,
      isMyProperties,
      isComparables,
      onModelRender
    } = this.props;

    const {
      drawing,
      drawCoords,
      cursorCoord,
      metric,
      infoProperty,
      parcelProperty,
      hoveredMarkerId,
      streetViewPosition,
      hovered,
      handfreeDrawing,
      handfreeDrawMode,
      isHandfreedrawing,
      drawMode
    } = this.state;

    const hasBoundary =
        (drawCoords && drawCoords.length >= 3) ||
        (this.state.circleCenter && this.state.circleRadius > 0);

    let cursorPolyline;
    let drawPolygon;

    if (isHandfreedrawing && drawCoords.length > 0) {
      // While actively hand-free drawing, show the path as a polyline (like drawing on paper)
      cursorPolyline = [...drawCoords];
      if (cursorCoord) {
        cursorPolyline.push(cursorCoord);
      }
    } else if (drawing && drawCoords.length > 0 && cursorCoord) {
      // Regular click-based drawing
      if (drawCoords.length < 3) {
        drawPolygon = [...drawCoords, cursorCoord];
      } else {
        cursorPolyline = [drawCoords[drawCoords.length - 1], cursorCoord];
        cursorPolyline.push(drawCoords[0]);
        drawPolygon = [...drawCoords];
      }
    }

    // Show closed polygon after handfree drawing is complete
    if (!isHandfreedrawing && drawCoords.length >= 3 && !handfreeDrawMode) {
      drawPolygon = [...drawCoords];
    }
    // if (drawCoords.length) {
    //   if (drawing && cursorCoord) {
    //     cursorPolyline = [drawCoords[drawCoords.length - 1], cursorCoord];
    //     if (drawCoords.length > 1) {
    //       cursorPolyline.push(drawCoords[0]);
    //     }
    //   }
    //   if (drawCoords.length + (cursorCoord ? 1 : 0) > 2) {
    //     drawPolygon = [...drawCoords];
    //     if (drawPolygon.length < 3) {
    //       drawPolygon.push(cursorCoord);
    //     }
    //   }
    // }

    const ul = window.UserLocation;
    const firstProperty = properties?.get?.(0);
    const firstPropertyType = firstProperty?.get?.('type');
    const { isFullMapView } = this.state;

    const mapOptions = (handfreeDrawMode || isHandfreedrawing)
        ? this.handfreeOptions
        : drawing
            ? this.drawOptions
            : drawMode === 'circle'
                ? this.circleOptions
                : this.defaultOptions;

    const isComparable = this.props.type?.get?.('type') === 'COMP';

    const drawControls =
        !onDraw || !this.state.mapContainer
            ? null
            : ReactDOM.createPortal(
                <div style={{
                  position: 'absolute',
                  top: '10px',
                  left: '10px',
                  right: '60px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  zIndex: 9999,
                  pointerEvents: 'none',
                }}>

                  <div>
                    <div
                        className={classNames(css.drawPill, {
                          [css.active]:
                          this.state.drawMode !== null ||
                          this.state.handfreeDrawMode ||
                          this.state.isHandfreedrawing,
                        })}
                        role="button"
                        tabIndex={0}
                        aria-pressed={drawing}
                        onClick={this.handleDrawIconClick}
                        onKeyDown={(e) =>
                            (e.key === 'Enter' || e.key === ' ') && this.handleDrawIconClick()
                        }
                    >
                      <img src={DrawActive} alt="" aria-hidden="true" className={css.iconDefault} />
                      <img src={DrawHover} alt="" aria-hidden="true" className={css.iconActive} />
                      <span className={css.label}>Draw</span>
                    </div>

                    {this.state.showDrawMenu && (
                        <div className={css.drawMenu}>

                          <div
                              className={classNames(css.drawOption, { [css.active]: this.state.drawMode === 'free' })}
                              role="button"
                              tabIndex={0}
                              aria-label="Free draw"
                              title="Free draw"
                              onClick={() => this.handleSelectDrawMode('free')}
                              onMouseEnter={() => this.handleDrawOptionMouseEnter('free')}
                              onMouseLeave={this.handleDrawOptionMouseLeave}
                          >
                            <div className={css.freeDrawIcon}>
                              <SVG icon="iconDrawFreeStroke" width={8} height={16} />
                              <SVG icon="iconDrawFreePencil" width={20} height={20} />
                            </div>
                          </div>

                          <div
                              className={classNames(css.drawOption, { [css.active]: this.state.drawMode === 'rectangle' })}
                              role="button"
                              tabIndex={0}
                              aria-label="Rectangle draw"
                              title="Rectangle draw"
                              onClick={() => this.handleSelectDrawMode('rectangle')}
                              onMouseEnter={() => this.handleDrawOptionMouseEnter('rectangle')}
                              onMouseLeave={this.handleDrawOptionMouseLeave}
                          >
                            <SVG icon="iconDrawRectangle" width={24} height={24} />
                          </div>

                          <div
                              className={classNames(css.drawOption, { [css.active]: this.state.drawMode === 'circle' })}
                              role="button"
                              tabIndex={0}
                              aria-label="Circle draw"
                              title="Circle draw"
                              onClick={() => this.handleSelectDrawMode('circle')}
                              onMouseEnter={() => this.handleDrawOptionMouseEnter('circle')}
                              onMouseLeave={this.handleDrawOptionMouseLeave}
                          >
                            <SVG icon="iconDrawCircle" width={24} height={25} />
                          </div>

                        </div>
                    )}

                    {this.state.showToast && !hasBoundary && (
                        <div className={css.draw} style={isComparable ? {
                          position: 'fixed',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          zIndex: 99999,
                          pointerEvents: 'auto',
                          top: '10px',
                        } : {}}>
                          <div className={css.drawInner}>
                            <div className={css.text}>
                              Click, drag or tap on the map to draw a shape in the area you want to search.
                            </div>
                            <div
                                className={classNames(css.buttonCustom, css.cancel)}
                                onClick={this.handleDrawCancel}
                                style={{ pointerEvents: 'auto' }}
                            >
                              Cancel
                            </div>
                          </div>
                        </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', pointerEvents: 'auto' }}>
                    {hasBoundary && (
                        <button
                            className={css.removeBoundaryBtn}
                            onClick={this.handleDrawCancel}
                            aria-label="Remove boundary"
                        >
                          <img src={iconClose} alt="" aria-hidden="true" className={`${css.iconClose} ${css.iconCloseDefault}`} />
                          <img src={WhiteClose} alt="" aria-hidden="true" className={`${css.iconClose} ${css.iconCloseWhite}`} />
                          Remove Boundary
                        </button>
                    )}

                    {!isFullMapView && analyticsEnabled && (
                        <Analytics metric={metric} onChange={this.handleMetricChange} />
                    )}
                  </div>

                </div >,
                this.state.mapContainer
            );

    return (
        <>
          <GoogleMap
              defaultZoom={initialZoom || ul.zoom}
              defaultMapTypeId={initialMapType}
              defaultCenter={{ lat: ul.latitude, lng: ul.longitude }}
              options={mapOptions}
              onClick={this.handleClick}
              onRightClick={this.handleRightClick}
              onMouseMove={this.handleMouseMove}
              ref={this.onRef}
          >
            {(properties || emptyMap)
                .filter((p) => p.get('latitude'))
                .slice(0, this.state.markersRendered)
                .map((p, i) => {
                  const id = p.get('id');
                  const info = infoEnabled && infoProperty?.get?.('id') === id;

                  const isSelected = infoProperty
                      ? infoProperty.get('id') === id
                      : this.props.activeResult === id;

                  const isHovered = hoveredMarkerId === id;
                  const anyMiniPinActive = this.props.allSelected || selectionCount || onSelectionDetails || onModelRender;
                  const useMiniPin = anyMiniPinActive || rangeEnabled;
                  const isSubjectProperty = isComparables && i === 0;

                  let labelText;
                  if (isMyProperties || (isComparables && !isSubjectProperty)) {
                    labelText = String(i + 1);
                  } else {
                    const estValue = p.get('estimatedValue');
                    const onMarket = p.get('mlsListingAmount');
                    const onMarketlistingAmount = p.get('listingAmount');
                    const marketStatus = p.get('onMarket');
                    const valueToDisplay = marketStatus ? (onMarket ?? (onMarketlistingAmount || estValue)) : estValue;
                    labelText = useMiniPin ? String(p.get('seq') || p.get('resultIndex') || ' ') : formatValue(valueToDisplay);
                  }

                  const zIndex = 100 +
                      (properties.size - i) +
                      (info ? 10000000 : 0) +
                      (isHovered ? 1000000000 : 0);

                  // Determine dimensions for the icon and label
                  const { iconWidth, iconHeight } = calcDims(useMiniPin, isSelected, isHovered);

                  // Street View Color Gradient Logic - REMOVED
                  const customPinStyle = {};
                  if (isStreetView && isSubjectProperty) {
                    customPinStyle.backgroundColor = '#CA1551';
                  }

                  const markerClass = classNames(css.mapPin, {
                    [css.mini]: useMiniPin,
                    [css.normal]: !useMiniPin,
                    [css.selected]: isSelected || isSubjectProperty,
                    [css.hovered]: isHovered,
                    [css.subjectProperty]: isSubjectProperty,
                    [css.streetViewPin]: isStreetView && !isSubjectProperty,
                  });

                  const fontSize = chooseFont(useMiniPin, labelText.length, isSelected || isSubjectProperty);

                  return (
                      <MemoMarker
                          key={id}
                          position={{ lat: p.get('latitude'), lng: p.get('longitude') }}
                          icon={{
                            url: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=',
                            scaledSize: new window.google.maps.Size(iconWidth, iconHeight),
                            labelOrigin: new window.google.maps.Point(iconWidth / 2, iconHeight / 2.5),
                            anchor: new window.google.maps.Point(iconWidth / 2, iconHeight / 2.5),
                          }}
                          label={{
                            text: labelText,
                            color: '#FFFFFF',
                            fontSize,
                            fontWeight: 'bold',
                            fontFamily: '$font-inter',
                            className: markerClass,
                            style: { ...customPinStyle },
                          }}
                          zIndex={zIndex}
                          isHovered={isHovered}
                          isSelected={isSelected || isSubjectProperty}
                          onOver={() => this.handleMarkerMouseOver(id)}
                          onOut={this.handleMarkerMouseOut}
                          onClick={() => {
                            this.setInfoProperty(p);
                            if (this.props.onMarkerClick) {
                              this.props.onMarkerClick(p.get('id'));
                            }
                          }}
                      >
                        {info && (
                            <InfoWindow onCloseClick={this.handleInfoClose}>
                              <PropertyInfo
                                  info={infoProperty}
                                  type={infoProperty && infoProperty.get('type')}
                                  history={this.props.history}
                                  handleSaveProperty={this.handleSaveProperty}
                                  isSaved={propertySaved}
                                  loading={loading}
                                  property={property}
                                  getProperty={getProperty}
                                  getPropertyContact={getPropertyContact}
                                  onCloseClick={this.handleInfoClose}
                                  searchType={firstPropertyType}
                                  onNearbyClick={this.props.onNearbyClick}
                                  store={this.context.store}
                              />
                            </InfoWindow>
                        )}
                      </MemoMarker>
                  );
                })}

            {!cursorPolyline ? null : (
                <Polyline path={cursorPolyline} options={{ ...drawProps }} />
            )}
            {drawMode !== 'circle' &&
                !this.state.circleCenter &&
                drawPolygon && (
                    <Polygon path={drawPolygon} options={drawProps} />
                )}
            {this.state.drawMode === "rectangle" && (
                <DrawingManager
                    defaultDrawingMode={window.google.maps.drawing.OverlayType.RECTANGLE}
                    options={{
                      drawingControl: false,
                      rectangleOptions: {
                        ...drawProps,
                        editable: true,
                        draggable: true,
                      },
                    }}
                    onRectangleComplete={this.handleRectangleComplete}
                />
            )}
            {this.state.circleCenter && this.state.circleRadius > 0 && (
                <>
                  <Circle
                      center={{
                        lat: this.state.circleCenter.lat(),
                        lng: this.state.circleCenter.lng(),
                      }}
                      radius={this.state.circleRadius}
                      options={{ ...drawProps, clickable: false }}
                  />

                  {[0, 90, 180, 270].map((deg) => {
                    const point = window.google.maps.geometry.spherical.computeOffset(
                        this.state.circleCenter,
                        this.state.circleRadius,
                        deg
                    );

                    return (
                        <Marker
                            key={`handle-${deg}`}
                            position={{ lat: point.lat(), lng: point.lng() }}
                            icon={{
                              path: window.google.maps.SymbolPath.CIRCLE,
                              scale: 6,
                              fillColor: '#FFFFFF',
                              fillOpacity: 1,
                              strokeColor: '#128292',
                              strokeWeight: 2,
                            }}
                            clickable={false}
                            zIndex={999}
                        />
                    );
                  })}
                </>
            )}

            {this.state.streetViewVisible && this.state.streetViewPosition && (
                <StreetViewPanorama
                    defaultPosition={this.state.streetViewPosition}
                    visible={true}
                    onPositionChanged={this.handleStreetViewPositionChange}
                />
            )}

            {!parcelProperty ? null : (
                <>
                  <Polygon path={parcelProperty.get('path')} options={drawProps} />
                  <MemoMarker
                      key="parcel"
                      position={parcelProperty.get('position')}
                      zIndex={1000000 + (parcelProperty.get('estimatedValue') || 0)}
                      onClick={() => this.setInfoProperty(parcelProperty)}
                      icon={{
                        url: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=',
                        scaledSize: new window.google.maps.Size(82, 38),
                        anchor: new window.google.maps.Size(41, 43),
                        labelOrigin: new window.google.maps.Point(41, 38),
                      }}
                      label={{
                        text: formatValue(
                            parcelProperty.get('onMarket') ? parcelProperty.getIn(['mlsListing', 'amount']) : parcelProperty.get('estimatedValue')
                        ),
                        color: '#FFFFFF',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        className: classNames(css.mapPin, css.normal, css.selected),
                      }}
                  >
                    <InfoWindow onCloseClick={this.handleInfoClose}>
                      <PropertyInfo
                          info={parcelProperty}
                          history={this.props.history}
                          handleSaveProperty={this.handleSaveProperty}
                          isSaved={propertySaved}
                          loading={loading}
                          property={property}
                          getProperty={getProperty}
                          getPropertyContact={getPropertyContact}
                          onCloseClick={this.handleInfoClose}
                          searchType={firstPropertyType}
                          onNearbyClick={this.props.onNearbyClick}
                          store={this.context.store}
                      />
                    </InfoWindow>
                  </MemoMarker>
                </>
            )}
          </GoogleMap>

          {drawControls}
          {/* {!analyticsEnabled ? null : (
          <Analytics metric={metric} onChange={this.handleMetricChange} />
        )} */}

        </>
    );
  }
}

Map.propTypes = {
  onDraw: PropTypes.func,
  onRemoveBoundary: PropTypes.func,
  infoEnabled: PropTypes.bool,
  analyticsEnabled: PropTypes.bool,
  parcelClickEnabled: PropTypes.bool,
  initialZoom: PropTypes.number,
  initialMapType: PropTypes.string,
  onNearbyClick: PropTypes.func,
  inputFlag: PropTypes.bool,
  hidePolygon: PropTypes.bool,
  autoOpenInfo: PropTypes.bool,
  allSelected: PropTypes.bool,
  isStreetView: PropTypes.bool,
  isMyProperties: PropTypes.bool,
  isComparables: PropTypes.bool,
  onMarkerClick: PropTypes.func,
};

Map.defaultProps = {
  infoEnabled: false,
  analyticsEnabled: false,
  parcelClickEnabled: false,
  initialMapType: 'roadmap',
  hidePolygon: false,
  autoOpenInfo: true,
  isStreetView: false,
  isMyProperties: false,
  isComparables: false,
};

const MapDetails = withProperty(Map, () => ({}), {});
export default withRouter(
    connect(
        (state, ownProps) => ({
          shapeDefinition: ownProps.shapeDefinition !== undefined
              ? ownProps.shapeDefinition
              : state.getIn(['search', 'location', 'shapeDefinition'], null),
          isStreetView: state.getIn(['streetView', 'isVisible'], false),
          isMyProperties: state.getIn(['myProperties', 'isViewingMap'], false),
          isComparables: state.getIn(['property', 'isViewingComparables'], false),
	  errors: state.get('errors'),
        }),
        { searchParcel }
    )(
        compose(
            withProps({
              loadingElement: <div style={{ height: '100%' }} />,
              containerElement: <div style={{ height: '100%', width: '100%' }} />,
              mapElement: <div style={{ height: '100%' }} />,
            }),
            withGoogleMap
        )(MapDetails)
    )
);